import { Component, OnDestroy, ViewContainerRef } from '@angular/core';
import { CommonCommunicationService } from './core/services/commonCommunication.service';
import { UserService } from './core/services/user.service';
import { Router } from '@angular/router';
import { TranslationService } from './core/services/translation.service';
import { TranslateService } from '@ngx-translate/core';
import { CommonUtilityService } from './core/services/commonUtility.service';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { telemetry } from './core/telemetry/telemetry';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnDestroy {
  isLoading: boolean = true;
  title = 'Socommerz Admin';
  userId: any;
  activeShopId: any;
  userData: any;
  selectedLang: any = 'en';
  shopDetails: any = {};
  isShopSetupCompleted: boolean = false;
  destroy$: any = new Subject<any>();

  constructor(
    private ccs: CommonCommunicationService,
    private commonUtilityService: CommonUtilityService,
    private userService: UserService,
    private translateService: TranslationService,
    private translate: TranslateService,
    private router: Router,
    // exposed so ngx-color-picker's cpUseRootViewContainer can render the picker at the app root
    public vcRef: ViewContainerRef
  ) {}

  async ngOnInit() {
    this.initTranslation();

    // Load token and user/shop details from localStorage
    const token: any = JSON.parse(localStorage.getItem('token') || '{}');
    this.userId = localStorage.getItem('userId');
    this.activeShopId = localStorage.getItem('activeShopId');

    // Set token and active shop ID in the common communication service
    this.ccs.setAuthToken(token);
    this.ccs.setActiveShopId(this.activeShopId);

    if (!this.userId) {
      this.isLoading = false;
      return;
    }

    this.userService
      .getTeamMemberById(this.userId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.userData = res;
          this.ccs.setUserData(res);
          this.identifyUser(res);
          this.loadShopAndRole();
        },
        error: (err) => {
          this.isLoading = false;
          telemetry.captureException(err, {
            tags: { boot_phase: 'user' },
            extra: { userId: this.userId }
          });
        }
      });
  }

  private identifyUser(user: any): void {
    if (!user) {
      return;
    }
    telemetry.setUser({
      id: user._id ?? user.id ?? this.userId,
      email: user.email,
      username: user.name,
      shopId: this.activeShopId
    });
  }

  private async initTranslation(): Promise<void> {
    const savedLang = localStorage.getItem('selectedLang') || 'en';
    this.selectedLang = savedLang;

    try {
      const res = await fetch(`/assets/i18n/${savedLang}.json`);
      if (res.ok) {
        this.translateService.setLanguage(savedLang);
        this.translate.use(savedLang);
      }
    } catch {}
  }

  private loadShopAndRole(): void {
    const activeRoleId = this.userData.shops.find((shop: any) => shop.shop_id === this.activeShopId)?.role_id;

    forkJoin({
      shop: this.userService.getShopById(this.activeShopId),
      role: this.userService.getRoleById(activeRoleId)
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ shop, role }) => {
          if (role?.data) {
            role.data.role_permission.user_role = role.data.role ?? '';
            this.ccs.setRolePermission(role.data.role_permission);
            this.ccs.setIsSuperAdmin(
              role.data.role === 'Super-Admin' && !!role.data.is_global && !!role.data.is_hidden
            );
          }

          if (shop?.result) {
            this.shopDetails = shop.result;
            this.ccs.setShopDetails(shop.result);
          }

          this.checkVerification();
        },
        error: (err) => {
          this.isLoading = false;
          telemetry.captureException(err, {
            tags: { boot_phase: 'shop_role' },
            extra: { userId: this.userId, activeShopId: this.activeShopId }
          });
        }
      });
  }

  private checkVerification(): void {
    this.isShopSetupCompleted = this.commonUtilityService.checkShopSetup(this.shopDetails);

    if (this.isShopSetupCompleted) {
      this.ccs.isSetupComplete.next(true);
    } else {
      this.ccs.isSetupComplete.next(false);
      this.router.navigate(['/onboarding']);
    }
    this.isLoading = false;
  }

  trackByIndex(index: number): number {
    return index;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
